import { useState, useEffect, useCallback, useRef } from "react"
import { useAuthContext } from "../hooks/useAuthContext"
import { useLtd } from "../hooks/useLtd"
import { useNavigate, Link } from "react-router-dom"
import Cookies from "js-cookie"

const MAX_CODES = 5
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000"
// Auth token lives in the `token` cookie (same pattern as billingApi.js / useLtd.js).
const authHeaders = () => {
    const t = Cookies.get("token")
    return t ? { Authorization: `Bearer ${t}` } : {}
}

// Tier pricing/limits are fetched from GET /api/ltd/tiers, which derives them from
// billing/ltdTiers.js — the same config that grants entitlements and powers /settings/plans.
// Never hardcode prices or caps here; they WILL drift and buyers will see wrong numbers.
const BEST_VALUE_TIER = 3

const fmtLimit = (n) => (n === -1 ? "Unlimited" : Number(n).toLocaleString())

// Feature rows rendered per tier with ✓ / ✗, derived from the API payload.
function tierRows(t) {
    return [
        { label: `${fmtLimit(t.limits.max_responses_per_month)} responses/month`, on: true },
        { label: `${fmtLimit(t.limits.max_ai_generations_per_month)} AI generations/month`, on: true },
        { label: `${t.integrations_count} integrations`, on: true },
        { label: "Unlimited forms", on: t.limits.max_active_forms === -1 },
        { label: "Conditional logic & conversational mode", on: t.features.conditional_logic },
        { label: "Remove ContentFlashAI branding", on: t.features.remove_branding },
        { label: "Response Intelligence Advanced", on: t.features.response_intelligence_advanced },
        {
            label: t.limits.max_workflows_per_form > 0
                ? `Agent Actions (${t.limits.max_workflows_per_form} workflow${t.limits.max_workflows_per_form > 1 ? "s" : ""}/form)`
                : "Agent Actions",
            on: t.features.agent_actions,
        },
        {
            label: t.limits.max_custom_classifiers === 0
                ? "Custom AI classifiers"
                : `${fmtLimit(t.limits.max_custom_classifiers)} custom AI classifiers`,
            on: t.limits.max_custom_classifiers !== 0,
        },
        { label: "Bring your own OpenAI key (BYOK)", on: t.features.byok_available },
        { label: "Custom thank-you pages", on: t.features.custom_thank_you_pages },
        { label: "Priority email support", on: t.features.priority_support },
    ]
}

export default function Redeem() {
    const { user } = useAuthContext()
    const navigate = useNavigate()
    const { status: ltdStatus, fetchStatus, validateCodes, redeemCodes } = useLtd()

    const [codes, setCodes] = useState([""])
    const [validation, setValidation] = useState(null)
    const [validating, setValidating] = useState(false)
    const [redeeming, setRedeeming] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState(null)
    const [turnstileToken, setTurnstileToken] = useState(null)
    const turnstileRef = useRef(null)
    const turnstileWidgetId = useRef(null)

    useEffect(() => {
        if (user) fetchStatus()
    }, [user, fetchStatus])

    useEffect(() => {
        // The widget container only mounts after codes validate (allValid), and the
        // Turnstile script loads async — so this must re-run on `validation` AND wait
        // for window.turnstile to be ready before rendering. The old [user]-only deps
        // meant the effect ran once on mount (before the div existed) and never again.
        if (!user || !turnstileRef.current) return
        const sitekey = process.env.REACT_APP_TURNSTILE_SITE_KEY
        if (!sitekey) return

        let cancelled = false
        let pollId = null

        const renderWidget = () => {
            if (cancelled || !turnstileRef.current || !window.turnstile) return
            if (turnstileWidgetId.current != null) return   // already rendered
            turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
                sitekey,
                callback: (token) => setTurnstileToken(token),
                "expired-callback": () => setTurnstileToken(null),
                "error-callback": () => setTurnstileToken(null)
            })
        }

        if (window.turnstile) {
            renderWidget()
        } else {
            // Script not loaded yet — poll until it is.
            pollId = setInterval(() => {
                if (window.turnstile) { clearInterval(pollId); pollId = null; renderWidget() }
            }, 200)
        }

        return () => {
            cancelled = true
            if (pollId) clearInterval(pollId)
            if (turnstileWidgetId.current != null) {
                window.turnstile?.remove(turnstileWidgetId.current)
                turnstileWidgetId.current = null
            }
        }
    }, [user, validation])

    const getCodes = () => codes.map(c => c.trim().toUpperCase()).filter(Boolean)

    const handlePaste = useCallback((e, idx) => {
        const pasted = e.clipboardData.getData("text")
        const split = pasted.split(/[\n,\s]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
        if (split.length > 1) {
            e.preventDefault()
            const merged = [...codes]
            merged.splice(idx, 1, ...split)
            setCodes(merged.slice(0, MAX_CODES))
            setValidation(null)
        }
    }, [codes])

    const handleValidate = async () => {
        const c = getCodes()
        if (!c.length) return
        setValidating(true)
        setError(null)
        setValidation(null)
        try {
            const res = await validateCodes(c)
            setValidation(res)
        } catch (e) {
            setError(e.message)
        } finally {
            setValidating(false)
        }
    }

    const handleRedeem = async () => {
        const validCodes = (validation?.results || []).filter(r => r.valid).map(r => r.code)
        if (!validCodes.length) return
        if (!turnstileToken) {
            setError("Please complete the human verification above.")
            return
        }
        setRedeeming(true)
        setError(null)
        try {
            const res = await redeemCodes(validCodes, turnstileToken)
            setResult(res)
        } catch (e) {
            setError(e.message)
            setTurnstileToken(null)
            if (turnstileWidgetId.current != null) window.turnstile?.reset(turnstileWidgetId.current)
        } finally {
            setRedeeming(false)
        }
    }

    const allValid = validation?.results?.every(r => r.valid) && validation.results.length > 0
    const summary = validation?.summary

    // Already has LTD
    const alreadyLtd = ltdStatus?.isLtd && !result

    if (result?.success) {
        return <SuccessScreen result={result} navigate={navigate} />
    }

    return (
        <div style={{ minHeight: "100vh", backgroundColor: "#f9fafb", fontFamily: "Inter, sans-serif" }}>
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", padding: "48px 24px", textAlign: "center", color: "white" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                    <AppSumoLogo />
                    <span style={{ fontSize: "14px", fontWeight: 600, opacity: 0.9 }}>AppSumo Lifetime Deal</span>
                </div>
                <h1 style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 800, margin: "0 0 12px" }}>
                    Redeem your lifetime code
                </h1>
                <p style={{ fontSize: "18px", opacity: 0.85, margin: 0, maxWidth: "500px", marginLeft: "auto", marginRight: "auto" }}>
                    Stack up to 5 codes to unlock more capacity. Pay once, use forever.
                </p>
            </div>

            <div style={{ maxWidth: "900px", margin: "0 auto", padding: "40px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "32px", alignItems: "start" }}>
                    {/* Left: code entry */}
                    <div>
                        {!user ? (
                            <div style={cardStyle}>
                                <h2 style={h2Style}>Sign in to redeem</h2>
                                <p style={{ color: "#6b7280", marginBottom: "20px" }}>
                                    You need to be logged in to apply your AppSumo code.
                                </p>
                                <div style={{ display: "flex", gap: "12px" }}>
                                    <Link to="/login?next=/redeem" style={{ ...btnStyle, background: "#6366f1", color: "white", textDecoration: "none" }}>
                                        Log in
                                    </Link>
                                    <Link to="/signup?next=/redeem" style={{ ...btnStyle, background: "white", color: "#374151", border: "1px solid #d1d5db", textDecoration: "none" }}>
                                        Create account
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <div style={cardStyle}>
                                {alreadyLtd && (
                                    <div style={{ background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: "10px", padding: "14px 16px", marginBottom: "24px", display: "flex", gap: "10px", alignItems: "flex-start" }}>
                                        <span style={{ fontSize: "20px" }}>🎉</span>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600, color: "#065f46" }}>
                                                You have an active LTD Tier {ltdStatus.tier}
                                            </p>
                                            <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#047857" }}>
                                                {ltdStatus.codeCount} code{ltdStatus.codeCount !== 1 ? "s" : ""} applied.{" "}
                                                {ltdStatus.canStackMore ? `Stack ${MAX_CODES - ltdStatus.codeCount} more to reach a higher tier.` : "You're at the maximum tier."}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <h2 style={h2Style}>Enter your AppSumo code{codes.length > 1 ? "s" : ""}</h2>
                                <p style={{ color: "#6b7280", fontSize: "14px", margin: "0 0 20px" }}>
                                    Paste multiple codes separated by lines or commas to stack them at once.
                                </p>

                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    {codes.map((c, i) => (
                                        <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                            <input
                                                type="text"
                                                value={c}
                                                onChange={e => { const n = [...codes]; n[i] = e.target.value; setCodes(n); setValidation(null) }}
                                                onPaste={e => handlePaste(e, i)}
                                                placeholder={`Code ${i + 1} — e.g., SUMO-A3FG-K9MN`}
                                                style={{ ...inputStyle, flex: 1, textTransform: "uppercase", fontFamily: "monospace", fontSize: "15px", letterSpacing: "0.05em" }}
                                            />
                                            {validation && (
                                                <StatusIcon valid={validation.results[i]?.valid} />
                                            )}
                                            {codes.length > 1 && (
                                                <button
                                                    onClick={() => { const n = codes.filter((_, j) => j !== i); setCodes(n.length ? n : [""]); setValidation(null) }}
                                                    style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "20px", lineHeight: 1, padding: "4px" }}
                                                    title="Remove"
                                                >×</button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {codes.length < MAX_CODES - (ltdStatus?.codeCount || 0) && (
                                    <button
                                        onClick={() => { setCodes([...codes, ""]); setValidation(null) }}
                                        style={{ marginTop: "10px", background: "none", border: "1px dashed #d1d5db", borderRadius: "8px", padding: "8px 14px", color: "#6b7280", cursor: "pointer", fontSize: "13px", width: "100%" }}
                                    >
                                        + Add another code
                                    </button>
                                )}

                                {error && (
                                    <p style={{ marginTop: "12px", color: "#ef4444", fontSize: "14px", padding: "10px 14px", background: "#fef2f2", borderRadius: "8px" }}>
                                        {error}
                                    </p>
                                )}

                                {validation && !allValid && (
                                    <div style={{ marginTop: "12px" }}>
                                        {validation.results.filter(r => !r.valid).map((r, i) => (
                                            <p key={i} style={{ color: "#ef4444", fontSize: "13px", margin: "4px 0" }}>
                                                <code>{r.code}</code>: {r.error}
                                            </p>
                                        ))}
                                    </div>
                                )}

                                {allValid && (
                                    <div ref={turnstileRef} style={{ marginTop: "20px" }} />
                                )}

                                <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
                                    {!validation || !allValid ? (
                                        <button
                                            onClick={handleValidate}
                                            disabled={validating || !getCodes().length}
                                            style={{ ...btnStyle, background: "#6366f1", color: "white", flex: 1 }}
                                        >
                                            {validating ? "Checking…" : "Check codes"}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleRedeem}
                                            disabled={redeeming || !turnstileToken}
                                            style={{ ...btnStyle, background: "#059669", color: "white", flex: 1, opacity: (!turnstileToken || redeeming) ? 0.6 : 1 }}
                                        >
                                            {redeeming ? "Activating…" : `Apply ${getCodes().length} code${getCodes().length > 1 ? "s" : ""} →`}
                                        </button>
                                    )}
                                </div>

                                {validation && allValid && summary && (
                                    <div style={{ marginTop: "16px", padding: "14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", fontSize: "14px", color: "#15803d" }}>
                                        ✓ {summary.validCount} code{summary.validCount > 1 ? "s" : ""} valid.{" "}
                                        {summary.isUpgrade ? (
                                            <>You&apos;ll upgrade from Tier {summary.currentTier || 0} to <strong>Tier {summary.newTier}</strong>.</>
                                        ) : (
                                            <>You&apos;ll unlock <strong>Tier {summary.newTier}</strong>.</>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <p style={{ marginTop: "16px", fontSize: "13px", color: "#9ca3af", textAlign: "center" }}>
                            Bought on AppSumo? Check your purchase confirmation email for your code(s).
                        </p>
                    </div>

                    {/* Right: tier preview */}
                    <div>
                        <TierPreview
                            tier={summary?.newTier || ltdStatus?.tier || 0}
                            currentCodes={ltdStatus?.codeCount || 0}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

// Renders the live tier table from GET /api/ltd/tiers. `tier` = the tier being previewed
// (post-redemption preview, or the user's current tier). `currentCodes` drives the
// "stack N more" progress line.
function TierPreview({ tier, currentCodes = 0 }) {
    const [data, setData] = useState(null)
    const [err, setErr] = useState(null)
    const [currency, setCurrency] = useState("USD")

    useEffect(() => {
        let alive = true
        fetch(`${API_BASE}/api/ltd/tiers`)
            .then(r => r.json())
            .then(d => { if (alive) setData(d) })
            .catch(e => { if (alive) setErr(e.message) })
        return () => { alive = false }
    }, [])

    // Default the currency to the user's billing currency when we know it.
    useEffect(() => {
        let alive = true
        fetch(`${API_BASE}/api/plan-state`, { headers: authHeaders() })
            .then(r => (r.ok ? r.json() : null))
            .then(ps => { if (alive && ps?.currency) setCurrency(ps.currency) })
            .catch(() => {})
        return () => { alive = false }
    }, [])

    if (err) return <div style={cardStyle}><p style={{ color: "#ef4444", fontSize: 13 }}>Couldn't load tiers: {err}</p></div>
    if (!data) return <div style={cardStyle}><p style={{ color: "#6b7280", fontSize: 13 }}>Loading tiers…</p></div>

    const activeTier = tier || 0
    const maxCodes = data.maxCodes || 5
    const nextTier = data.tiers.find(t => t.codes === currentCodes + 1)
    const price = (t) => currency === "INR"
        ? `₹${Number(t.price_inr).toLocaleString("en-IN")}`
        : `$${Number(t.price_usd).toLocaleString()}`

    return (
        <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>Lifetime tiers</h3>
                <div style={{ display: "flex", gap: 4 }}>
                    {["USD", "INR"].map(c => (
                        <button key={c} type="button" onClick={() => setCurrency(c)}
                            style={{
                                padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
                                border: "1px solid " + (currency === c ? "#6366f1" : "#e5e7eb"),
                                background: currency === c ? "#6366f1" : "white",
                                color: currency === c ? "white" : "#6b7280",
                            }}>{c}</button>
                    ))}
                </div>
            </div>

            {/* Progress: what the next code unlocks */}
            {currentCodes > 0 && currentCodes < maxCodes && nextTier && (
                <div style={{ marginBottom: 14, padding: "10px 12px", background: "#eef2ff", borderRadius: 10, fontSize: 13, color: "#4338ca" }}>
                    You're on <strong>Tier {currentCodes}</strong>. Stack <strong>1 more code</strong> ({nextTier.codes} total) to unlock{" "}
                    <strong>Tier {nextTier.tier}</strong> — {fmtLimit(nextTier.limits.max_responses_per_month)} responses/mo,{" "}
                    {fmtLimit(nextTier.limits.max_ai_generations_per_month)} AI generations/mo.
                </div>
            )}
            {currentCodes >= maxCodes && (
                <div style={{ marginBottom: 14, padding: "10px 12px", background: "#ecfdf5", borderRadius: 10, fontSize: 13, color: "#047857" }}>
                    🎉 You're on the maximum tier — every lifetime feature is unlocked.
                </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {data.tiers.map(t => {
                    const isActive = t.tier === activeTier
                    const isOwned = currentCodes >= t.codes
                    const isBest = t.tier === BEST_VALUE_TIER
                    return (
                        <div key={t.tier} style={{
                            position: "relative", padding: "12px 14px", borderRadius: 12,
                            border: "1px solid " + (isActive ? "#6366f1" : isBest ? "#c7d2fe" : "#e5e7eb"),
                            background: isActive ? "#eef2ff" : "white",
                        }}>
                            {isBest && (
                                <span style={{
                                    position: "absolute", top: -9, right: 12, padding: "2px 8px", borderRadius: 999,
                                    fontSize: 10, fontWeight: 700, letterSpacing: .4, background: "#6366f1", color: "white",
                                }}>BEST VALUE</span>
                            )}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                                <strong style={{ fontSize: 15, color: "#111827" }}>
                                    Tier {t.tier} {isOwned && <span style={{ color: "#059669", fontSize: 12 }}>· active</span>}
                                </strong>
                                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                                    {price(t)}
                                    <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>
                                        {" "}/ {t.codes} code{t.codes > 1 ? "s" : ""}
                                        {currency === "INR" ? ` + ${data.gstPercent}% GST` : ""}
                                    </span>
                                </span>
                            </div>
                            <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "flex", flexDirection: "column", gap: 4 }}>
                                {tierRows(t).map((row, i) => (
                                    <li key={i} style={{
                                        display: "flex", gap: 7, alignItems: "flex-start", fontSize: 13,
                                        color: row.on ? "#374151" : "#9ca3af",
                                    }}>
                                        <span style={{ flexShrink: 0, color: row.on ? "#6366f1" : "#d1d5db" }}>{row.on ? "✓" : "✗"}</span>
                                        <span style={row.on ? undefined : { textDecoration: "line-through" }}>{row.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                })}
            </div>

            <div style={{ marginTop: 16, padding: "12px 14px", background: "#faf5ff", borderRadius: 10, fontSize: 13, color: "#7c3aed" }}>
                <strong>Agency features</strong> (white-label, multi-workspace) are available as a separate add-on for LTD users — your lifetime deal is the foundation.
            </div>
        </div>
    )
}

function SuccessScreen({ result, navigate }) {
    return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", fontFamily: "Inter, sans-serif", padding: "24px" }}>
            <div style={{ ...cardStyle, maxWidth: "500px", textAlign: "center" }}>
                <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "32px" }}>
                    🎉
                </div>
                <h2 style={{ margin: "0 0 8px", fontSize: "24px", fontWeight: 800, color: "#111827" }}>
                    Lifetime Deal Activated!
                </h2>
                <p style={{ margin: "0 0 8px", fontSize: "16px", color: "#6b7280" }}>
                    You&apos;re now on <strong style={{ color: "#6366f1" }}>AppSumo LTD Tier {result.redemption?.tierUnlocked}</strong>.
                </p>
                <p style={{ margin: "0 0 28px", fontSize: "14px", color: "#9ca3af" }}>
                    Check your email for onboarding tips and community links.
                </p>
                <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                    <button
                        onClick={() => navigate("/form-builder")}
                        style={{ ...btnStyle, background: "#6366f1", color: "white" }}
                    >
                        Create my first form →
                    </button>
                    <button
                        onClick={() => navigate("/settings?tab=billing")}
                        style={{ ...btnStyle, background: "white", color: "#374151", border: "1px solid #d1d5db" }}
                    >
                        View LTD details
                    </button>
                </div>
                {result.redemption?.codeCount < 5 && (
                    <p style={{ marginTop: "20px", fontSize: "13px", color: "#9ca3af" }}>
                        Stack {5 - result.redemption.codeCount} more code{5 - result.redemption.codeCount !== 1 ? "s" : ""} to reach a higher tier.
                    </p>
                )}
            </div>
        </div>
    )
}

function StatusIcon({ valid }) {
    if (valid === undefined) return null
    return (
        <span style={{ fontSize: "18px", flexShrink: 0 }}>
            {valid ? "✓" : "✗"}
        </span>
    )
}

function AppSumoLogo() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="6" fill="#FF6B00" />
            <text x="4" y="18" fontSize="16" fontWeight="bold" fill="white">S</text>
        </svg>
    )
}

// Styles
const cardStyle = {
    background: "white",
    borderRadius: "16px",
    padding: "28px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 8px 24px rgba(0,0,0,0.06)"
}
const h2Style = { margin: "0 0 8px", fontSize: "20px", fontWeight: 700, color: "#111827" }
const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    border: "1.5px solid #d1d5db",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    color: "#111827",
    background: "white"
}
const btnStyle = {
    padding: "12px 22px",
    borderRadius: "10px",
    border: "none",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "opacity 0.15s"
}
